import { useMutation } from '@apollo/client';
import AddActionButton from '@components/AddActionButton';
import { ButtonSaveType } from '@components/ButtonSaveSplit';
import { confirm } from '@components/ConfirmModal/confirm';
import DashedDivider from '@components/Divider/DashedDivider';
import { FlexRow } from '@components/FlexRow';
import FormContainer from '@components/FormContainer';
import InputDate from '@components/InputDate';
import InputDocuments from '@components/InputDocuments';
import PreviewOrDownloadFileButton from '@components/InputDocuments/PreviewOrDownloadFileButton';
import InputFieldControlled from '@components/InputFieldControlled';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import Text from '@components/Text';
import { ADD_CERTIFICATE_TALENT, DELETE_CERTIFICATE_TALENT, UPDATE_CERTIFICATE_TALENT } from '@gql/certificate';
import { GET_TALENT } from '@gql/talents';
import { checkPropertiesIsHaveValueWithIncludes } from '@helper/validation.utils';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Menu, MenuItem, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { moment } from '@utils/date.utils';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SectionCard } from '../SectionCard';
import { SectionTitle } from '../SectionTitle';
import EmptyCertificateIllustration from "./empty-certificate.svg";

// Styled Components
const CheckboxContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
}));

const CertificateItemContainer = styled(Box)(({ theme }) => ({
    borderRadius: '1rem',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    '&.error': {
        backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    },
    "&:first-child": {
        paddingTop: theme.spacing(0),
    },
    "&:last-child": {
        paddingBottom: theme.spacing(0),
    },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
}));


const ErrorMessageContainer = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
}));

type Props = {
    certification?: any;
    onCancel?: () => void;
    onSubmitted?: () => void;
    isEdit?: boolean;
    talentId?: string;
};
const CertificationForm = ({ onCancel, onSubmitted, talentId, ...props }: Props) => {
    const [loading, setLoading] = useState(false);
    const [formState, setFormState] = useState<'view' | 'edit'>('edit');
    const [saveType, setSaveType] = useState<ButtonSaveType>(ButtonSaveType.SAVE_AND_RETURN);
    const [certification, setCertification] = useState<any>(props.certification);
    const [randomKey, setRandomKey] = useState(Math.random());
    const defaultValues = useMemo(() => ({
        issuer: certification?.issuer?.name ?? null,
        title: certification?.title ?? null,
        number: certification?.number ?? null,
        url: certification?.url ?? null,
        profession: certification?.profession?._id ?? null,
        startDate: certification?.startDate ? moment(certification?.startDate).format('YYYY-MM-DD') : null,
        endDate: certification?.endDate ? moment(certification?.endDate).format('YYYY-MM-DD') : null,
        file: null,
    }), [certification]);
    const [lifeTime, setLifeTime] = useState(certification?.endDate ? false : true);
    const { control, handleSubmit, getValues, setValue, watch, clearErrors, setError } = useForm({
        mode: 'all',
        defaultValues,
    });

    // snackbar
    const { showSnackbar } = useSnackbar();

    const [save] = useMutation(ADD_CERTIFICATE_TALENT);
    const [update] = useMutation(UPDATE_CERTIFICATE_TALENT);

    const submitHandler = useCallback(async certificateInput => {
        setLoading(true);
        if (!certification?._id) {
            await save({
                variables: {
                    certificateInput,
                    talentId: talentId
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: {
                            talentId: talentId
                        },
                    },
                ],
            });
            showSnackbar('Certification ajoutée avec succès', 'success');
        } else {
            await update({
                variables: {
                    _id: certification?._id,
                    certificateInput,
                    talentId: talentId
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: {
                            talentId: talentId,
                        },
                    },
                ],
            });
            showSnackbar('Certification modifiée avec succès', 'success');
        }
        if (saveType === ButtonSaveType.SAVE_AND_RETURN) {
            onSubmitted();
        } else {
            Object.keys(defaultValues).forEach(key => {
                setValue(key as keyof typeof defaultValues, null);
            });
            setRandomKey(Math.random());
            setCertification(null);
        }
        setLoading(false);
    }, [certification?._id, saveType, save, talentId, showSnackbar, update, onSubmitted, defaultValues, setValue]);

    return (
        <FormContainer
            onBack={onCancel}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            onCancel={onCancel}
            onSubmit={handleSubmit(submitHandler)}
            onButtonSplitChange={type => {
                setSaveType(type);
            }}
            submitText={props.isEdit ? 'Enregistrer' : 'Suivant'}
            buttonSplitText={{
                [ButtonSaveType.SAVE_AND_RETURN]: 'Enregistrer',
                [ButtonSaveType.SAVE_AND_ADD]: 'Ajouter un nouveau certificat',
            }}
            formTitle={formState === 'view' ? 'Détails de la certification' : props.isEdit ? 'Modifier la certification' : 'Ajouter une certification'}
            formDescription={formState === 'view' ? 'Voici les détails de la certification.' : props.isEdit ? 'Modifiez les informations de la certification.' : 'Remplissez les informations de la certification.'}
        >
            <InputFieldControlled
                name="title"
                control={control}
                label="Titre de la certifications"
                placeholder="ex : UX Design  Coursera Certification  by Google"
                required
            />
            <InputFieldControlled
                name="issuer"
                control={control}
                label="Nom de l'organisme émetteur"
                placeholder="Ex : Coursera"
                required
            />

            <Stack
                flexDirection={{
                    xs: 'column',
                    sm: 'row',
                }}
                gap={2}
                alignItems={'flex-start'}>
                <InputDate
                    name="startDate"
                    control={control}
                    label="Date  d'obtention"
                    placeholder="Date  d'obtention"
                    onChange={val => {
                        if (
                            moment(val).isAfter(moment(getValues('endDate'))) ||
                            moment(val).isSame(moment(getValues('endDate')))
                        ) {
                            setValue('endDate', moment(val).add(1, 'day').format('YYYY-MM-DD'));
                        }
                    }}
                    required
                />
                <InputDate
                    name="endDate"
                    control={control}
                    minDate={watch('startDate') ? moment(watch('startDate')).add('1', 'day') : undefined}
                    label="Date d'expiration"
                    placeholder="Date d'expiration"
                    onChange={val => {
                        setLifeTime(!!val ? false : true);
                    }}
                    required={!lifeTime}
                />            </Stack>
            {/* CHECKBOX LIFETIME */}
            <CheckboxContainer>
                <Checkbox
                    id="checkbox-lifetime"
                    checked={lifeTime && watch('endDate') === null}
                    onChange={e => {
                        if (e.target.checked) {
                            setValue('endDate', null);
                            // remove error from endDate required
                            clearErrors('endDate');
                        } else {
                            // set error to endDate required
                            setError('endDate', {
                                type: 'required',
                                message: "La date d'expiration est obligatoire si le certificat expire",
                            });
                        }
                        setLifeTime(!!e.target.checked);
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
                <Text darkGray size={16} error={isEmpty(watch('endDate')) && !lifeTime}>
                    Ce certificat n’expire jamais
                </Text>
            </CheckboxContainer>
            <InputFieldControlled name="number" control={control} label="Numéro du certificat" />
            <InputFieldControlled name="url" control={control} label="URL du certificat" />
            <InputDocuments
                key={randomKey}
                name="file"
                control={control}
                label="Certificat"
                placeholder="Files"
                file={certification?.file}
            />
        </FormContainer>
    );
};

export const CertificateItem = ({
    certificate,
    onClick = undefined,
    onDelete = undefined,
    divider = true,
    isDeleting = false,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
    const theme = useTheme();

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        onClick?.();
    };

    const handleDelete = () => {
        handleMenuClose();
        onDelete?.();
    };

    const hasError = !checkCertificationValue(certificate).status;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    };

    const formatPeriod = () => {
        const start = formatDate(certificate.startDate);
        const end = certificate.endDate ? formatDate(certificate.endDate) : 'Sans expiration';
        return start && end ? `${start} - ${end}` : start || end || '';
    };

    return (
        <>
            <CertificateItemContainer
                className={hasError ? 'error' : ''}
                sx={{ position: 'relative' }}
            >
                {/* Logo */}
                <Box
                    sx={{
                        width: isMobile ? 32 : 46,
                        height: isMobile ? 32 : 46,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <img
                        src={certificate.issuer.logo || '/icons/default-issuer-logo.jpg'}
                        width={isMobile ? 32 : 46}
                        height={isMobile ? 32 : 46}
                        alt="Certificate Logo"
                        style={{ borderRadius: '8px' }}
                    />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Typography
                            sx={{
                                fontFamily: 'LEMON MILK Pro',
                                fontSize: isMobile ? '14px' : '18px',
                                fontWeight: 700,
                                lineHeight: 'normal',
                                color: theme.palette.neutralWhite.main,
                                flex: 1,
                            }}>
                            {certificate?.title || 'Titre du certificat'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', height: '20px' }}>
                            <Typography variant="body1" fontWeight={700} sx={{
                                color: '#CBCBCB',
                                fontSize: isMobile ? '12px' : '16px'
                            }}>
                                {certificate?.issuer?.name || 'Organisme émetteur'}
                            </Typography>
                            {certificate?.number && (
                                <>
                                    <Box
                                        sx={{
                                            width: '6px',
                                            height: '6px',
                                            borderRadius: '50%',
                                            background: '#CBCBCB',
                                        }}
                                    />
                                    <Typography variant="body1" fontWeight={700} sx={{
                                        color: '#CBCBCB',
                                        fontSize: isMobile ? '12px' : '16px'
                                    }}>
                                        {certificate.number}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
                        {formatPeriod() && (
                            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <i className="fi fi-br-time-oclock" style={{ fontSize: '13px', color: '#CBCBCB' }} />
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{
                                        color: '#CBCBCB',
                                        fontSize: '13px',
                                        lineHeight: '16px',
                                    }}>
                                    {formatPeriod()}
                                </Typography>
                            </Box>
                        )}
                        {/* File Download/Preview */}
                        {certificate?.file?.name && (
                            <PreviewOrDownloadFileButton
                                action="preview"
                                hideFileSize
                                file={certificate.file}
                            />
                        )}
                    </Box>
                    {certificate?.obtainedResult && (
                        <Typography variant="body2" sx={{ color: '#cbcbcb', fontSize: '14px' }}>
                            Résultat : {certificate.obtainedResult}
                        </Typography>
                    )}
                </Box>

                {/* Action Menu */}
                {onClick && onDelete && (
                    <ActionButtonsContainer>
                        <IconButton
                            disabled={isDeleting}
                            aria-label="more"
                            aria-controls={open ? 'action-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleMenuOpen}
                            size="small"
                            sx={{
                                borderRadius: '4px',
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.action.hover,
                                },
                            }}
                        >
                            <MoreVertIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                        <Menu
                            id="action-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            MenuListProps={{
                                'aria-labelledby': 'action-button',
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={handleEdit}>
                                <i
                                    className="fi fi-rr-edit"
                                    style={{
                                        marginRight: '8px',
                                        fontSize: isMobile ? 14 : 16
                                    }}
                                />
                                Modifier
                            </MenuItem>
                            <MenuItem onClick={handleDelete}>
                                <i
                                    className="fi fi-rr-trash"
                                    style={{
                                        marginRight: '8px',
                                        fontSize: isMobile ? 14 : 16,
                                        color: '#d32f2f'
                                    }}
                                />
                                Supprimer
                            </MenuItem>
                        </Menu>
                    </ActionButtonsContainer>
                )}
            </CertificateItemContainer>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleEdit}>
                    <i className="fi fi-br-edit" style={{ fontSize: '16px', marginRight: '8px' }} />
                    Modifier
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: theme.palette.errorRed.main }}>
                    <i className="fi fi-br-trash" style={{ fontSize: '16px', marginRight: '8px' }} />
                    Supprimer
                </MenuItem>
            </Menu>
            {divider && <DashedDivider />}
        </>
    );
};

export const CertificateEmpty = () => {
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
    return (
        <Stack p={3} gap={3} justifyContent={'center'} alignItems={'center'}>
            <img src={EmptyCertificateIllustration} alt="Aucune certification trouvée" />
            <Stack maxWidth={isMobile ? 435 : 600} gap={1}>
                <Typography textAlign="center">
                    Ajoutez vos certifications professionnelles
                </Typography>
                <Typography textAlign="center" color="textSecondary" fontSize={isMobile ? 12 : 16}>
                    Ajoutez une certification et validez des compétences
                </Typography>
            </Stack>
        </Stack>
    );
};

export const checkCertificationValue = certificate => {
    if (!certificate.current) {
        return checkPropertiesIsHaveValueWithIncludes(certificate, ['title', 'issuer', 'startDate', 'endDate']);
    }
    return checkPropertiesIsHaveValueWithIncludes(certificate, ['title', 'issuer', 'startDate']);
};

const TalentCertificatetList = ({ talent }) => {
    const [selectedCertification, setSelectedCertification] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [deleteCertificatet] = useMutation(DELETE_CERTIFICATE_TALENT, {});
    const [hasError, setHasError] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        setHasError(
            talent?.certificates?.some(certificate => {
                return !checkCertificationValue(certificate).status;
            }),
        );
    }, [talent?.certificates]);

    const handleDelete = useCallback(_id => {
        (async () => {
            if (await confirm({ confirmation: 'Êtes-vous sûr de vouloir supprimer cette certification?' })) {
                setDeletingId(_id);
                try {
                    await deleteCertificatet({
                        variables: {
                            _id,
                            talentId: talent?._id
                        },
                        refetchQueries: () => [
                            {
                                query: GET_TALENT,
                                variables: { talentId: talent?._id }
                            },
                        ],
                    });
                } finally {
                    setDeletingId(null);
                }
            } else {
                console.log('cancel cancelled');
            }
        })();
    }, [deleteCertificatet, talent?._id]);

    if (openForm) {
        return <CertificationForm
            talentId={talent?._id}
            isEdit
            certification={selectedCertification}
            onCancel={() => {
                setOpenForm(false);
                setSelectedCertification(null);
            }}
            onSubmitted={() => {
                setOpenForm(false);
                setSelectedCertification(null);
            }}
        />
    }

    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <FlexRow gap={1} alignItems="center" justifyContent="space-between">
                <SectionTitle title="Certification" withInfo />
                <AddActionButton
                    onClick={() => {
                        setOpenForm(true);
                        setSelectedCertification(null);
                    }}
                />
            </FlexRow>

            {hasError && (
                <ErrorMessageContainer>
                    <Text size={14} sx={{ color: theme.palette.errorRed.main }}>
                        Veuillez compléter les informations manquantes ou supprimer la formation encadrée de
                        rouge.
                    </Text>
                </ErrorMessageContainer>
            )}
            {/* EDUCATION ITEM */}
            {talent?.certificates?.map((certificate, index) => (
                <React.Fragment key={certificate?._id}>
                    <CertificateItem
                        onClick={() => {
                            setOpenForm(true);
                            setSelectedCertification(certificate);
                        }}
                        onDelete={() => handleDelete(certificate?._id)}
                        certificate={certificate}
                        divider={index !== talent?.certificates?.length - 1}
                        isDeleting={deletingId === certificate?._id}
                    />
                </React.Fragment>
            ))}
            {/* EMPTHY STATE */}
            {(talent?.certificates?.length === 0 || !talent) && <CertificateEmpty />}
        </SectionCard>
    );
};

export default TalentCertificatetList;
